import DefaultAvatar from 'assets/images/avatar.png';
import PropTypes from 'prop-types';
import { RESTRICTED_KEYS } from 'shared/fixtures';
import { accountActions } from 'core/Account';
import { config } from 'config';
import { images } from 'styled';
import isEmpty from 'lodash/isEmpty';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    ConfirmationModal,
    LanguageModal,
    MobileMenu,
    SkipToContent,
    UserMenu
} from 'views/components';
import {
    Container,
    Divider,
    FlagContainer,
    Image,
    Nav
} from './styled';
import {
    Flag,
    Menu,
    Text
} from 'mol-ui';
import React, { useRef, useState } from 'react';
import {
    getHideStatistics,
    isLocked,
    isSuperadmin,
    parseCountryLanguage
} from 'shared/utils';
import {
    useDispatch,
    useSelector,
    useWindowSize
} from 'hooks';
import {
    useHistory,
    useLocation
} from 'react-router-dom';

const {
    absenceReport,
    availableLanguages,
    appointmentTypes,
    areas,
    campaigns,
    company,
    controls,
    costCenters,
    coverages,
    offices,
    patientListing,
    prework,
    polyOffices,
    positions,
    recipesFiles,
    recipesRequests,
    recording,
    redirectAfterLogin,
    restrictedMenu,
    riskAgents,
    turns,
    version,
    videoconference,
    workplaces
} = config;
const {
    absenteeism,
    diagnostics,
    general,
    licenses,
    patients,
    preworks,
    professionals,
    recommendations,
    sessions,
    statistics: hideStatistics,
    surveys,
    symptoms,
    turns: statisticsTurns,
    videocall
} = getHideStatistics();

const { logo_monochrome } = images;

const Layout = ({
    children
}) => {

    const dispatch = useDispatch();
    const {
        logoutModal,
        user
    } = useSelector('account');

    const contentRef = useRef(null);

    const language = localStorage.getItem('i18nextLng');
    const { push } = useHistory();
    const { pathname } = useLocation();
    const { overPrimaryColor } = useTheme();
    const { t } = useTranslation();
    const [isSidebarOpened, setIsSidebarOpened] = useState(false);
    const [languageModal, setLanguageModal] = useState(false);

    const { lg } = useWindowSize();

    const handleLogout = value => {
        dispatch(accountActions.accountLogoutModalToggle());
        if (value === 'confirm') {
            dispatch(accountActions.logoutAccount());
        }
    };

    const { avatar_thumbnail, first_name, last_name, restricted_access } = user;
    const showLanguagePicker = availableLanguages.length > 1;

    const Footer = () => <Text>{ `v${ version }` }</Text>;

    const baseMenuItems = [{
        icon: 'home',
        path: t('/dashboard'),
        restrictedKey: RESTRICTED_KEYS.dashboard,
        text: t('Dashboard')
    },
    {
        icon: 'people_alt',
        path: t('/profesionales/listar'),
        restrictedKey: RESTRICTED_KEYS.professionals,
        text: t('Profesionales')
    },
    {
        display: patientListing,
        icon: 'people_outline',
        path: t('/pacientes/listar'),
        restrictedKey: RESTRICTED_KEYS.patients,
        text: t('Pacientes')
    },
    {
        icon: 'account_circle',
        path: t('/usuarios/listar'),
        restricted: true,
        text: t('Usuarios')
    },
    {
        display: absenceReport,
        icon: 'person_off',
        path: '/ausencias',
        restrictedKey: RESTRICTED_KEYS.absences,
        text: t('Ausencias')
    },
    {
        display: campaigns,
        icon: 'campaign',
        path: '/campanas',
        restrictedKey: RESTRICTED_KEYS.campaigns,
        text: t('Campañas')
    }, {
        icon: 'admin_panel_settings',
        items: [{
            display: riskAgents,
            icon: 'emergency',
            path: t('/agentes'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Agentes de riesgo')
        }, {
            display: positions,
            icon: 'manage_accounts',
            path: t('/cargos'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Cargos')
        }, {
            display: costCenters,
            icon: 'point_of_sale',
            path: t('/centros'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Centros de costos')
        }, {
            display: coverages,
            icon: 'create_new_folder',
            path: t('/coberturas/listar'),
            restrictedKey: RESTRICTED_KEYS.coverages,
            text: t('Coberturas')
        }, {
            display: offices,
            icon: 'apartment',
            path: t('/consultorios/listar'),
            restrictedKey: RESTRICTED_KEYS.offices,
            text: t('Consultorios')
        }, {
            display: controls,
            icon: 'checklist',
            path: t('/controles'),
            restrictedKey: RESTRICTED_KEYS.controls,
            text: t('Controles')
        }, {
            display: company,
            icon: 'business',
            path: t('/empresas'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Empresas')
        }, {
            icon: 'folder_special',
            path: t('/especialidades'),
            restrictedKey: RESTRICTED_KEYS.statistics,
            text: t('Especialidades')
        }, {
            display: workplaces,
            icon: 'home_work',
            path: t('/lugares'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Lugares de trabajo')
        }, {
            display: polyOffices,
            icon: 'location_city',
            path: t('/policonsultorios'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Policonsultorios')
        }, {
            display: areas,
            icon: 'place',
            path: t('/zonas'),
            restrictedKey: RESTRICTED_KEYS.manage,
            text: t('Zonas')
        }],
        restrictedKey: RESTRICTED_KEYS.manage,
        text: t('Gestión')
    },
    {
        display: videoconference,
        icon: 'videocam',
        path: t('/videoconferencia/listar'),
        text: t('Videoconferencias')
    },
    {
        display: turns,
        icon: 'date_range',
        items: [{
            icon: 'event',
            path: t('/turnos'),
            permission: 'appointments',
            restrictedKey: RESTRICTED_KEYS.appointments,
            text: t('Calendario')
        },
        {
            icon: 'edit_calendar',
            path: t('/agenda'),
            permission: 'appointments',
            restrictedKey: RESTRICTED_KEYS.appointments,
            text: t('Agendas')
        },
        {
            display: appointmentTypes,
            icon: 'settings',
            path: t('/consultas/tipos/listar'),
            permission: 'appointments',
            restrictedKey: RESTRICTED_KEYS.appointmentTypes,
            text: t('Tipos de consulta')
        }],
        permission: 'appointments',
        restrictedKey: RESTRICTED_KEYS.appointments,
        text: t('Turnos')
    },
    {
        display: recording || recipesFiles,
        icon: 'settings',
        path: t('/configuracion'),
        restrictedKey: RESTRICTED_KEYS.configuration,
        text: t('Configuración')
    },
    {
        icon: 'mail_lock',
        onlySuperadmin: true,
        path: t('/sys/notificaciones'),
        text: t('Notificaciones')
    },
    {
        icon: 'lock',
        onlySuperadmin: true,
        path: t('/sys/configuracion'),
        text: t('Plataforma')
    },
    {
        display: !hideStatistics,
        icon: 'bar_chart',
        items: [{
            display: !absenteeism,
            icon: 'show_chart',
            path: t('/estadisticas/ausentismo'),
            text: t('Ausentismo')
        },  {
            display: !preworks || prework,
            icon: 'show_chart',
            path: t('/estadisticas/controles-ocupacionales'),
            text: t('C. Ocupacionales')
        }, {
            display: !diagnostics,
            icon: 'show_chart',
            path: t('/estadisticas/diagnosticos'),
            text: t('Diagnósticos')
        }, {
            display: !surveys,
            icon: 'show_chart',
            path: t('/estadisticas/encuestas'),
            text: t('Encuestas')
        }, {
            display: !general,
            icon: 'show_chart',
            path: t('/estadisticas/general'),
            text: t('General')
        }, {
            display: !licenses,
            icon: 'show_chart',
            path: t('/estadisticas/licencias'),
            text: t('Licencias')
        }, {
            display: !patients,
            icon: 'show_chart',
            path: t('/estadisticas/pacientes'),
            text: t('Pacientes')
        }, {
            display: !professionals,
            icon: 'show_chart',
            path: t('/estadisticas/profesionales'),
            text: t('Profesionales')
        }, {
            display: recipesRequests,
            icon: 'show_chart',
            path: t('/estadisticas/recetas'),
            text: t('Recetas')
        }, {
            display: !recommendations,
            icon: 'show_chart',
            path: t('/estadisticas/recomendaciones'),
            text: t('Recomendaciones')
        }, {
            display: !sessions,
            icon: 'show_chart',
            path: t('/estadisticas/sesiones'),
            text: t('Sesiones')
        }, {
            display: !symptoms,
            icon: 'show_chart',
            path: t('/estadisticas/sintomas'),
            text: t('Síntomas')
        }, {
            display: turns && !statisticsTurns,
            icon: 'show_chart',
            path: t('/estadisticas/turnos'),
            text: t('Turnos')
        }, {
            display: !videocall,
            icon: 'show_chart',
            path: t('/estadisticas/videollamada'),
            text: t('Videollamada')
        }],
        permission: 'view_statistics',
        restrictedKey: RESTRICTED_KEYS.statistics,
        text: t('Estadísticas')
    }];

    const parseMenuItems = list => list.map(item => {
        const {
            display,
            items,
            onlySuperadmin,
            permission,
            restricted,
            restrictedKey
        } = item;

        const isAllowed = !permission || !isLocked(user, permission);
        const isNotRestricted = (!restricted && !restrictedMenu.includes(restrictedKey)) || !restricted_access;
        const isNotSuperadminLocked = !onlySuperadmin || isSuperadmin(user?.email);
        const shouldDisplay = (display !== false) && isAllowed && isNotRestricted && isNotSuperadminLocked;
        
        return ({
            ...item,
            display: shouldDisplay,
            ...(!isEmpty(items) && { items: parseMenuItems(items) })
        });
    });

    const menuItems = parseMenuItems(baseMenuItems);

    const handleLogoClick = () => {
        setIsSidebarOpened(false);
        push(redirectAfterLogin ?? t('/dashboard'));
    };

    const handleMenuClick = item => {
        const { altKey, key, metaKey, path } = item;
        setIsSidebarOpened(false);
        if (!!key) return;
        if (altKey || metaKey) return window.open(path, '_blank');
        return push(path);
    };

    return (
        <>
            { !!contentRef?.current && <SkipToContent /> }
            <Nav>
                <MobileMenu isOpen={ isSidebarOpened } onToggle={ () => setIsSidebarOpened(isSidebarOpened => !isSidebarOpened) } />
                <Image>
                    <img alt="logo" src={ logo_monochrome } onClick={ () => handleLogoClick() } />
                </Image>
                <Divider />
                <LanguageModal
                    active={ languageModal }
                    onToggle={ () => setLanguageModal(false) }
                />
                {
                    showLanguagePicker &&
                    <FlagContainer onClick={ () => setLanguageModal(true) }>
                        <Flag borderColor={ overPrimaryColor } country={ parseCountryLanguage(language) } />
                    </FlagContainer>
                }
                <UserMenu
                    avatar={ avatar_thumbnail?.url ?? DefaultAvatar }
                    userName={ `${ last_name }, ${ first_name }` }
                />
            </Nav>
            <ConfirmationModal
                actionHandler={ handleLogout }
                active={ logoutModal }
                confirmText={ t('Salir') }
                title={ t('¿Está seguro que desea salir?') }
            />
            <Menu
                active={ isSidebarOpened || lg }
                currentPath={ pathname }
                footer={ <Footer /> }
                items={ menuItems }
                top="70px"
                zIndex={ lg ? 1 : 2 }
                onClick={ handleMenuClick }
            />
            <Container id="content" ref={ contentRef } >
                { children }
            </Container>
        </>
    );

};

Layout.propTypes = {
    children: PropTypes.any
};

export default Layout;
